import { useCallback, useEffect, useRef } from "react";
import clsx from "clsx";
import { Button } from "@/components/ui/Button";
import {
  SIGNATURE_STROKE_CSS_PX,
  imageDataHasInk,
  pointerToCanvasPoint,
  signatureBackingStoreSize,
} from "@/features/timesheetFlow/components/signatureCanvasGeometry";

const PAD_HEIGHT_PX = 180;

export function SignatureCanvas({
  className,
  onChange,
}: {
  className?: string;
  onChange?: (hasStrokes: boolean, dataUrl: string | null) => void;
}) {
  const hostRef = useRef<HTMLDivElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const drawingRef = useRef(false);
  const hasStrokesRef = useRef(false);
  const lastPosRef = useRef({ x: 0, y: 0 });
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const contextOf = (canvas: HTMLCanvasElement) =>
    canvas.getContext("2d", { willReadFrequently: true });

  /** Identitätstransformation: Zeichenkoordinaten sind bereits Bitmap-Pixel. */
  const prepareInk = (ctx: CanvasRenderingContext2D, bitmapPerCssPixel: number) => {
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.strokeStyle = "#111827";
    ctx.fillStyle = "#111827";
    ctx.lineWidth = SIGNATURE_STROKE_CSS_PX * bitmapPerCssPixel;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
  };

  const bitmapScale = (canvas: HTMLCanvasElement) => {
    const rect = canvas.getBoundingClientRect();
    return rect.width > 0 ? canvas.width / rect.width : 1;
  };

  /** toDataURL ist teuer – nur bei Stroke-Ende / Leeren, nie pro Pointer-Move. */
  const emitChange = useCallback((includeDataUrl: boolean) => {
    const canvas = canvasRef.current;
    const cb = onChangeRef.current;
    if (!canvas || !cb) return;
    if (!hasStrokesRef.current) {
      cb(false, null);
      return;
    }
    if (!includeDataUrl) {
      cb(true, null);
      return;
    }
    const ctx = contextOf(canvas);
    let pixels: Uint8ClampedArray | null = null;
    try {
      pixels = ctx?.getImageData(0, 0, canvas.width, canvas.height).data ?? null;
    } catch {
      pixels = null;
    }
    if (pixels && !imageDataHasInk(pixels)) {
      hasStrokesRef.current = false;
      cb(false, null);
      return;
    }
    cb(true, canvas.toDataURL("image/png"));
  }, []);

  const getPos = (canvas: HTMLCanvasElement, point: { clientX: number; clientY: number }) => {
    const rect = canvas.getBoundingClientRect();
    return pointerToCanvasPoint(point.clientX, point.clientY, rect, canvas.width, canvas.height);
  };

  const drawLine = (x: number, y: number) => {
    const canvas = canvasRef.current;
    const ctx = canvas ? contextOf(canvas) : null;
    if (!canvas || !ctx) return;
    prepareInk(ctx, bitmapScale(canvas));
    ctx.beginPath();
    ctx.moveTo(lastPosRef.current.x, lastPosRef.current.y);
    ctx.lineTo(x, y);
    ctx.stroke();
    lastPosRef.current = { x, y };
    hasStrokesRef.current = true;
  };

  const stampDot = (x: number, y: number) => {
    const canvas = canvasRef.current;
    const ctx = canvas ? contextOf(canvas) : null;
    if (!canvas || !ctx) return;
    const scale = bitmapScale(canvas);
    prepareInk(ctx, scale);
    ctx.beginPath();
    ctx.arc(x, y, (SIGNATURE_STROKE_CSS_PX * scale) / 2, 0, Math.PI * 2);
    ctx.fill();
    hasStrokesRef.current = true;
  };

  const handlePointerDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (e.pointerType === "mouse" && e.button !== 0) return;
    e.preventDefault();
    const canvas = e.currentTarget;
    try {
      canvas.setPointerCapture(e.pointerId);
    } catch {
      // Zeichnen auch dann starten, wenn der Browser den Pointer nicht einfängt.
    }
    drawingRef.current = true;
    const pos = getPos(canvas, e);
    lastPosRef.current = pos;
    stampDot(pos.x, pos.y);
    emitChange(false);
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!drawingRef.current) return;
    e.preventDefault();
    const canvas = canvasRef.current;
    if (!canvas) return;
    const native = e.nativeEvent;
    const coalesced =
      typeof native.getCoalescedEvents === "function" ? native.getCoalescedEvents() : [];
    const points = coalesced.length > 0 ? coalesced : [native];
    for (const point of points) {
      const pos = getPos(canvas, point);
      drawLine(pos.x, pos.y);
    }
  };

  const endStroke = () => {
    if (!drawingRef.current) return;
    drawingRef.current = false;
    emitChange(true);
  };

  const handlePointerUp = (e: React.PointerEvent<HTMLCanvasElement>) => {
    try {
      if (e.currentTarget.hasPointerCapture(e.pointerId)) {
        e.currentTarget.releasePointerCapture(e.pointerId);
      }
    } catch {
      // Capture kann bereits beendet sein.
    }
    endStroke();
  };

  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas ? contextOf(canvas) : null;
    if (!canvas || !ctx) return;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    hasStrokesRef.current = false;
    drawingRef.current = false;
    emitChange(false);
  };

  useEffect(() => {
    const host = hostRef.current;
    const canvas = canvasRef.current;
    if (!host || !canvas) return;

    const syncSize = () => {
      const cssWidth = host.clientWidth;
      const cssHeight = host.clientHeight;
      if (cssWidth < 2 || cssHeight < 2) return;
      const next = signatureBackingStoreSize(cssWidth, cssHeight, window.devicePixelRatio || 1);
      if (canvas.width === next.width && canvas.height === next.height) return;

      const copy = hasStrokesRef.current ? snapshotCanvas(canvas) : null;
      canvas.width = next.width;
      canvas.height = next.height;
      const ctx = contextOf(canvas);
      if (ctx && copy) {
        ctx.setTransform(1, 0, 0, 1, 0, 0);
        ctx.drawImage(copy, 0, 0, next.width, next.height);
      }
    };

    syncSize();
    const observer = new ResizeObserver(() => syncSize());
    observer.observe(host);
    return () => observer.disconnect();
  }, []);

  return (
    <div className={className}>
      <div ref={hostRef} className="w-full min-w-0" style={{ height: PAD_HEIGHT_PX }}>
        <canvas
          ref={canvasRef}
          className={clsx(
            "block h-full w-full touch-none select-none rounded-ios border border-separator bg-white",
          )}
          style={{ touchAction: "none" }}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerUp}
          aria-label="Unterschrift zeichnen"
        />
      </div>
      <div className="mt-2 flex justify-end">
        <Button type="button" variant="secondary" size="sm" onClick={clear}>
          Leeren
        </Button>
      </div>
    </div>
  );
}

function snapshotCanvas(source: HTMLCanvasElement): HTMLCanvasElement | null {
  if (source.width < 1 || source.height < 1) return null;
  const copy = document.createElement("canvas");
  copy.width = source.width;
  copy.height = source.height;
  const ctx = copy.getContext("2d");
  if (!ctx) return null;
  ctx.drawImage(source, 0, 0);
  return copy;
}
